import { Divider, Skeleton } from "antd";
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

const InvoiceView = ({ handleInvoiceGenerate, invoice, resDetails, invoiceLoading }) => {
    const { t } = useTranslation(["reservations"]);

    // console.log("InvoiceView rendered", { invoice, resDetails, invoiceLoading });

    useEffect(() => {
        handleInvoiceGenerate();
    }, [handleInvoiceGenerate]);

    if ((!invoiceLoading && !invoice) || !resDetails) {
        return null;
    }

    const pickupDateObject = new Date(resDetails?.pickupDate);
    const dropoffDateObject = new Date(resDetails?.dropoffDate);

    const pickupDate = pickupDateObject.toLocaleDateString();
    const pickupTime = pickupDateObject.toLocaleTimeString();
    const dropoffDate = dropoffDateObject.toLocaleDateString();
    const dropoffTime = dropoffDateObject.toLocaleTimeString();

    const alreadyPaid = Number(invoice?.alreadyPaid ?? 0);
    const dueAmount = Math.max(
        0,
        Math.round((Number(invoice?.total || 0) - alreadyPaid) * 100) / 100,
    );

    return (
        <div>
            <h2 className="mb-3 text-xl font-semibold sm:mb-4 sm:text-2xl">{t("carSwapModal.invoice")}</h2>
            <div className="rounded-lg border p-3 sm:mx-10 sm:p-6 md:mx-28 md:p-8">
                <div className="flex items-center justify-between">
                    <p>{t("carSwapModal.pickupLocation")}</p>
                    <p className="font-semibold">
                        {resDetails?.pickupLocation?.city
                            ? `${resDetails?.pickupLocation?.city}, ${resDetails?.pickupLocation?.state}`
                            : ""}
                    </p>
                </div>
                <div className="flex items-center justify-between">
                    <p>{t("carSwapModal.pickupDate")}</p>
                    <p className="font-semibold">{pickupDate}</p>
                </div>
                <div className="flex items-center justify-between">
                    <p>{t("carSwapModal.pickupTime")}</p>
                    <p className="font-semibold">{pickupTime}</p>
                </div>
                <div className="flex items-center justify-between">
                    <p>{t("carSwapModal.dropOffDate")}</p>
                    <p className="font-semibold">{dropoffDate}</p>
                </div>
                <div className="flex items-center justify-between">
                    <p>{t("carSwapModal.dropOffTime")}</p>
                    <p className="font-semibold">{dropoffTime}</p>
                </div>

                <Divider />

                {invoiceLoading && (
                    <div className="flex flex-col gap-2">
                        <Skeleton.Input active block size="small" />
                        <Skeleton.Input active block size="small" />
                        <Skeleton.Input active block size="small" />
                        <Skeleton.Input active block size="small" />
                        <Skeleton.Input active block size="small" />
                        <Skeleton.Input active block size="small" />
                    </div>
                )}

                {!invoiceLoading && (
                    <>
                        {invoice?.charges?.map((item, index) => (
                            <div className="flex items-center justify-between" key={index}>
                                <p className="capitalize">{`${item?.description} x ${item.unit}`}</p>
                                <p className="capitalize font-semibold">${item.total?.toFixed(2)}</p>
                            </div>
                        ))}

                        <Divider className="my-1" />

                        <div className="mb-4 flex items-center justify-between">
                            <p className="font-semibold capitalize">{t("carSwapModal.estimatedTotal")}</p>
                            <p className="font-semibold">${invoice?.estimatedTotal?.toFixed(2)}</p>
                        </div>

                        {invoice?.taxes?.map((item, index) => (
                            <div key={index} className="flex items-center justify-between">
                                <div className="capitalize">
                                    {item?.taxName}{" "}
                                    {item?.taxType === "%" ? `(${item?.taxValue}%)` : `($${item?.taxValue})`}
                                </div>
                                <p className="capitalize font-semibold">${item?.total.toFixed(2)}</p>
                            </div>
                        ))}

                        <Divider className="my-1" />

                        <div className="flex items-center justify-between">
                            <p className="font-semibold">{t("carSwapModal.total")}</p>
                            <div className="font-semibold">${invoice.total?.toFixed(2)}</div>
                        </div>

                        {alreadyPaid > 0 ? (
                            <div className="mt-2 flex items-center justify-between text-[#667085]">
                                <p>{t("carSwapModal.alreadyPaid")}</p>
                                <p className="font-semibold">-${alreadyPaid.toFixed(2)}</p>
                            </div>
                        ) : null}

                        <div className="mt-3 flex items-center justify-between rounded-lg bg-[#F0F7FF] px-3 py-2">
                            <p className="font-semibold text-[#5D5DF6]">{t("carSwapModal.amountDue")}</p>
                            <p className="text-lg font-bold text-[#5D5DF6]">${dueAmount.toFixed(2)}</p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

InvoiceView.propTypes = {
    handleInvoiceGenerate: PropTypes.func.isRequired,
    invoice: PropTypes.shape({
        charges: PropTypes.arrayOf(
            PropTypes.shape({
                description: PropTypes.string,
                unit: PropTypes.number,
                total: PropTypes.number,
            }),
        ),
        estimatedTotal: PropTypes.number,
        taxes: PropTypes.arrayOf(
            PropTypes.shape({
                taxName: PropTypes.string,
                taxType: PropTypes.string,
                taxValue: PropTypes.number,
                total: PropTypes.number,
            }),
        ),
        total: PropTypes.number,
        due: PropTypes.number,
        alreadyPaid: PropTypes.number,
    }),
    resDetails: PropTypes.shape({
        pickupDate: PropTypes.string,
        dropoffDate: PropTypes.string,
        pickupLocation: PropTypes.shape({
            city: PropTypes.string,
            state: PropTypes.string,
        }),
    }),
    invoiceLoading: PropTypes.bool.isRequired,
};

export default InvoiceView;
